import React, { useCallback, useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { Box, Typography, Container, Grid } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGlobalContext } from "Context/GlobalContext";
import { useStatisticsContext } from "Context/StatisticsPageContext";
import ChartSection from "Organisms/ChartSection/ChartSection";
import Map from "Moleculas/Home/Map";
import StatisticsCards from "Moleculas/StatisticsCards/StatisticsCards";
import styles from "./EstadisticasStyles";
import { ICO_DOWNLOAD } from "Assets/index";

import {
  getMissingByYearAndSex,
  getMissingComparable,
  getMissingViolenceFiscalia,
  getWomenMissingByAgeRange,
  getMenMissingByAgeRange,
  getLocatedByConditionAndCrime,
  getPercentageByYear,
  getPercentageComparable,
  getWomenLocatedByCrime,
  getMenLocatedByCrime,
  getConSinCarpeta,
} from "Api/graphics";

import {
  mapYearBySex,
  mapViolenceBySexAndYear,
  mapYearByAgeRange,
  mapConditionByCrime,
  mapPercentageByYear,
  formatFootnoteSexBreakdown,
  formatFootnoteViolenceBreakdown,
  formatFootnoteAgeBreakdown,
  mapInvestigationCI,
  mapInvestigationReport,
} from "Utils/statisticsDataMappers";

import {
  mockMissingByYearData,
  mockMissingComparableData,
  mockMissingViolenceData,
  mockWomenMissingAgeData,
  mockMenMissingAgeData,
  mockLocatedConditionCrimeData,
  mockPercentageByYearData,
  mockPercentageComparableData,
  mockWomenLocatedCrimeData,
  mockMenLocatedCrimeData,
  mockInvestigationCIData,
  mockInvestigationReportData,
} from "Utils/mockStatisticsData";

import {
  SERIES_BY_SEX,
  SERIES_VIOLENCE_BY_SEX,
  SERIES_WOMEN_AGE_RANGE,
  SERIES_MEN_AGE_RANGE,
  SERIES_CONDITION_CRIME,
  SERIES_PERCENTAGE,
  SERIES_WOMEN_CONDITION_CRIME,
  SERIES_MEN_CONDITION_CRIME,
  SERIES_INVESTIGATION_CI,
  SERIES_INVESTIGATION_REPORT,
} from "Utils/statisticsSeries";


const extractMeta = (data) => ({ anteriores: data.anteriores_2019 });
const extractG11Meta = (data) => ({ xMaxDomain: data.Total_Desapariciones });

const CHART_REQUESTS = [
  { key: "g1Data",  request: getMissingByYearAndSex,       fallback: mockMissingByYearData,         mapper: mapYearBySex,            metaExtractor: extractMeta },
  { key: "g2Data",  request: getMissingComparable,          fallback: mockMissingComparableData,     mapper: mapYearBySex,            metaExtractor: extractMeta },
  { key: "g3Data",  request: getMissingViolenceFiscalia,    fallback: mockMissingViolenceData,       mapper: mapViolenceBySexAndYear,  metaExtractor: extractMeta },
  { key: "g4Data",  request: getWomenMissingByAgeRange,     fallback: mockWomenMissingAgeData,       mapper: mapYearByAgeRange,       metaExtractor: extractMeta },
  { key: "g5Data",  request: getMenMissingByAgeRange,       fallback: mockMenMissingAgeData,         mapper: mapYearByAgeRange,       metaExtractor: extractMeta },
  { key: "g6Data",  request: getLocatedByConditionAndCrime, fallback: mockLocatedConditionCrimeData, mapper: mapConditionByCrime },
  { key: "g7Data",  request: getPercentageByYear,           fallback: mockPercentageByYearData,      mapper: mapPercentageByYear },
  { key: "g8Data",  request: getPercentageComparable,       fallback: mockPercentageComparableData,  mapper: mapPercentageByYear },
  { key: "g9Data",  request: getWomenLocatedByCrime,        fallback: mockWomenLocatedCrimeData,     mapper: mapConditionByCrime },
  { key: "g10Data", request: getMenLocatedByCrime,          fallback: mockMenLocatedCrimeData,       mapper: mapConditionByCrime },
  { key: "g11_1Data", request: getConSinCarpeta, fallback: mockInvestigationCIData, mapper: mapInvestigationCI, metaExtractor: extractG11Meta },
  { key: "g11_2Data", request: getConSinCarpeta, fallback: mockInvestigationReportData, mapper: mapInvestigationReport, metaExtractor: extractG11Meta },
];

const INITIAL_CHART_DATA = {
  g1Data:   mockMissingByYearData,
  g2Data:   mockMissingComparableData,
  g3Data:   mockMissingViolenceData,
  g4Data:   mockWomenMissingAgeData,
  g5Data:   mockMenMissingAgeData,
  g6Data:   mockLocatedConditionCrimeData,
  g7Data:   mockPercentageByYearData,
  g8Data:   mockPercentageComparableData,
  g9Data:   mockWomenLocatedCrimeData,
  g10Data:  mockMenLocatedCrimeData,
  g11_1Data: mockInvestigationCIData,
  g11_2Data: mockInvestigationReportData,
};

const Estadisticas = () => {
  const { fullDateFormat, updateDate, apiUrl } = useGlobalContext();
  const { handleGetCardData } = useStatisticsContext();
  const isMobileScreen = useMediaQuery("(max-width:768px)");

  const [downloadBtnSmall, setDownloadBtnSmall] = useState(isMobileScreen);
  const [downloadBtnAnimatedFirstTime, setDownloadBtnAnimatedFirstTime] = useState(false);
  const [chartData, setChartData] = useState(INITIAL_CHART_DATA);
  const [chartMeta, setChartMeta] = useState({});

  const year = updateDate.format("YYYY");
  const dayMonth = `${updateDate.format("DD")} de ${updateDate.format("MMMM")}`;

  // --- Scroll handler para el botón de descarga ---
  const handleScroll = useCallback(() => {
    if (isMobileScreen) {
      setDownloadBtnSmall(true);
      return;
    }
    if (window.scrollY > 200) {
      setDownloadBtnSmall(true);
      setDownloadBtnAnimatedFirstTime(true);
      return;
    }
    setDownloadBtnSmall(false);
  }, [isMobileScreen]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (isMobileScreen) {
      setDownloadBtnSmall(true);
    }
  }, [isMobileScreen]);

  useEffect(() => {
    handleGetCardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchChartData = async () => {
      const results = await Promise.allSettled(
        CHART_REQUESTS.map(({ request }) => request())
      );

      const newData = {};
      const newMeta = {};
      results.forEach((result, index) => {
        const { key, fallback, mapper, metaExtractor } = CHART_REQUESTS[index];
        if (result.status === "fulfilled") {
          newData[key] = mapper(result.value.data);
          if (metaExtractor) {
            newMeta[key] = metaExtractor(result.value.data);
          }
        } else {
          newData[key] = fallback;
        }
      });

      setChartData((prev) => ({ ...prev, ...newData }));
      setChartMeta((prev) => ({ ...prev, ...newMeta }));
    };

    fetchChartData();
  }, []);

  const handleClickDownload = () => {
    ReactGA.event({
      category: "Estadisticas",
      action: "Descargo excel datos abiertos",
    });
  };

  const buildFootnotes = (metaKey, anterioresFormatter, staticNotes = []) => {
    const meta = chartMeta[metaKey];
    const notes = [...staticNotes];
    if (meta?.anteriores) {
      const formatted = anterioresFormatter(meta.anteriores);
      if (formatted) {
        if (Array.isArray(formatted)) notes.push(...formatted);
        else notes.push(formatted);
      }
    }
    return notes;
  };

  return (
    <Box component="main" sx={{ backgroundColor: "#FAFAFA" }}>
      <Container maxWidth={false} sx={{ px: 0, pb: { xs: 5, sm: 10 }, pt: { xs: 8, sm: 11 } }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "#1C5866", fontSize: "50px" }}>
                Estadísticas
              </Typography>
              <Typography sx={{ color: "#3B4348", fontSize: "15px" }}>
                {`*Información actualizada al ${updateDate.format("DD/MM/YYYY")}`}
              </Typography>
              <Box
                sx={{
                  position: "fixed",
                  right: { xs: "10px", md: "100px" },
                  bottom: "100px",
                  overflow: "hidden",
                  top: { md: "150px" },
                  zIndex: 2,
                  height: "3rem",
                }}
              >
                <a
                  href={`${apiUrl}version_publica/exportrebdboptimizado/xls/`}
                  onClick={handleClickDownload}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                  download
                >
                  <Box
                    sx={{
                      ...(downloadBtnSmall
                        ? styles.animatedButtonSmall
                        : downloadBtnAnimatedFirstTime
                          ? styles.animatedButton
                          : {}),
                      py: 1,
                      fontWeight: "bold",
                      borderRadius: 50,
                      px: "35px",
                      margin: "auto",
                      display: "flex",
                      backgroundColor: "#C0E4D9",
                      color: "#1C5866",
                      textTransform: "capitalize !important",
                      "&:hover": { backgroundColor: "#C0E4D9" },
                    }}
                  >
                    <Box
                      component={"img"}
                      src={ICO_DOWNLOAD}
                      alt="Descargar datos"
                      sx={{
                        width: "20px",
                        height: "20px",
                        marginRight: "30px",
                        ...(downloadBtnSmall
                          ? styles.animatedButtonIconSmall
                          : downloadBtnAnimatedFirstTime
                            ? styles.animatedButtonIcon
                            : {}),
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        ...(downloadBtnSmall
                          ? styles.animatedButtonTextSmall
                          : downloadBtnAnimatedFirstTime
                            ? styles.animatedButtonText
                            : {}),
                        color: "#1C5866",
                        fontSize: "15px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Descargar datos
                    </Typography>
                  </Box>
                </a>
              </Box>
            </Box>
            {/* <StatisticsCards /> */}
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ color: "#1C5866", fontSize: "44px", mt: 5, mb: 3, textAlign: "center" }}>
              Personas desaparecidas
            </Typography>
            <Box sx={{ width: "calc(100vw - 50px)", margin: "auto", maxWidth: "100%" }}>
              {/* G11.1: Total desapariciones vs con carpeta de investigación */}
              <ChartSection
                title="Personas desaparecidas con carpeta de investigación"
                subtitle={`Datos a fecha de corte de ${fullDateFormat}`}
                data={chartData.g11_1Data}
                series={SERIES_INVESTIGATION_CI}
                type="stacked"
                height={100}
                xMaxOverride={chartMeta.g11_1Data?.xMaxDomain}
              />

              {/* G11.2: Total desapariciones vs sólo con reporte ante COBUPEJ */}
              <ChartSection
                title="Personas desaparecidas sin carpeta de investigación"
                subtitle={`Datos a fecha de corte de ${fullDateFormat}`}
                data={chartData.g11_2Data}
                series={SERIES_INVESTIGATION_REPORT}
                type="stacked"
                height={100}
                xMaxOverride={chartMeta.g11_2Data?.xMaxDomain}
              />

              {/* G1: Personas desaparecidas por año de desaparición y sexo */}
              <ChartSection
                title={`Personas desaparecidas, por año de desaparición y sexo (2019-${year})`}
                subtitle={`Datos al ${fullDateFormat}`}
                data={chartData.g1Data}
                series={SERIES_BY_SEX}
                type="stacked"
                footnotes={buildFootnotes("g1Data", formatFootnoteSexBreakdown, 
                  ["En la gráfica se muestran sólo los datos de desapariciones ocurridas a partir del 1 de enero de 2019."])}
              />

              {/* G2: Personas desaparecidas periodo comparable */}
              <ChartSection
                title={`Personas desaparecidas del 1 de enero al ${dayMonth} de 2019 a ${year}`}
                subtitle={`Datos al ${fullDateFormat}`}
                data={chartData.g2Data}
                series={SERIES_BY_SEX}
                type="stacked"
                footnotes={buildFootnotes("g2Data", formatFootnoteSexBreakdown, 
                [`En la gráfica se muestran sólo los datos de desapariciones ocurridas 
                  y reportadas del 1 de enero al mes de corte de 2019 a ${year}
                  ` ])}
              />

              {/* G3: Desapariciones con y sin violencia  */}
              <ChartSection
                title={`Desapariciones con violencia y sin violencia, por año de desaparición (2019-${year})`}
                subtitle={`Datos al ${fullDateFormat}`}
                data={chartData.g3Data}
                series={SERIES_VIOLENCE_BY_SEX}
                type="stacked"
                footnotes={buildFootnotes("g3Data", formatFootnoteViolenceBreakdown, 
                  [`En la gráfica se muestran sólo los datos de desapariciones ocurridas a partir del 1 de enero 
                    de 2019 y que cuentan con carpeta de investigación ante la Vicefiscalía en Personas Desaparecidas.`])}
              />

              {/* G4: Mujeres desaparecidas por año y rango de edad */}
              <ChartSection
                title={`Mujeres desaparecidas, por año de desaparición y rango de edad (2019-${year})`}
                subtitle={`Datos al ${fullDateFormat}`}
                data={chartData.g4Data}
                series={SERIES_WOMEN_AGE_RANGE}
                type="stacked"
                footnotes={buildFootnotes("g4Data", (ant) => formatFootnoteAgeBreakdown(ant, "MUJER"),
                [`En la gráfica se muestran sólo los datos de mujeres cuya desaparición ocurrió a partir del 1 de enero de 2019`])}
              />

              {/* G5: Hombres desaparecidos por año y rango de edad */}
              <ChartSection
                title={`Hombres desaparecidos, por año de desaparición y rango de edad (2019-${year})`}
                subtitle={`Datos al ${fullDateFormat}`}
                data={chartData.g5Data}
                series={SERIES_MEN_AGE_RANGE}
                type="stacked"
                footnotes={buildFootnotes("g5Data", (ant) => formatFootnoteAgeBreakdown(ant, "HOMBRE"),
                [`En la gráfica se muestran sólo los datos de hombres cuya desaparición ocurrió a partir del 1 de enero de 2019`])}
              />

              <Typography sx={{ color: "#1C5866", fontSize: "44px", mt: 5, mb: 3, textAlign: "center" }}>
                Personas localizadas
              </Typography>
              {/* G6: Personas localizadas por condición y tipo de delito */}
              <ChartSection
                title="Personas localizadas, por condición de localización y si fueron o no víctimas de delito"
                subtitle={`Datos del 1 de diciembre de 2018 al ${fullDateFormat}`}
                data={chartData.g6Data}
                series={SERIES_CONDITION_CRIME}
                type="stacked"
                height={150}
              />

              {/* G7: Porcentaje de localización por año */}
              <ChartSection
                title={`Porcentaje de localización por año (2019-${year})`}
                subtitle={`Datos del 1 de diciembre de 2018 al ${fullDateFormat}`}
                data={chartData.g7Data}
                series={SERIES_PERCENTAGE}
                type="stacked"
                footnotes={[
                  "Refleja el porcentaje de personas que fueron localizadas en el año, respecto del total de personas cuya desaparición y reporte ocurrieron entre el 1 de enero y el 31 de diciembre de ese año.",
                ]}
              />

              {/* G8: Porcentaje de localización periodo comparable */}
              <ChartSection
                title={`Porcentaje de localización del 1 de enero al ${dayMonth} de 2019 a ${year}`}
                subtitle={`Datos del 1 de diciembre de 2018 al ${fullDateFormat}`}
                data={chartData.g8Data}
                series={SERIES_PERCENTAGE}
                type="stacked"
                footnotes={[
                  `Refleja el porcentaje de personas que fueron localizadas en el periodo del 1 de enero al ${dayMonth} de cada año, respecto del total de personas cuya desaparición y reporte ocurrieron ese mismo periodo de cada año.`,
                ]}
              />

              {/* G9: Mujeres localizadas por condición y tipo de delito */}
              <ChartSection
                title="Mujeres localizadas, por condición de localización y si fueron o no víctimas de delito"
                subtitle={`Datos del 1 de diciembre de 2018 al ${fullDateFormat}`}
                data={chartData.g9Data}
                series={SERIES_WOMEN_CONDITION_CRIME}
                type="stacked"
                height={150}
              />

              {/* G10: Hombres localizados por condición y tipo de delito */}
              <ChartSection
                title="Hombres localizados, por condición de localización y si fueron o no víctimas de delito"
                subtitle={`Datos del 1 de diciembre de 2018 al ${fullDateFormat}`}
                data={chartData.g10Data}
                series={SERIES_MEN_CONDITION_CRIME}
                type="stacked"
                height={150}
              />
            </Box>

            <Typography sx={{ color: "#1C5866", fontSize: "44px", mt: 5, mb: 3, textAlign: "center" }}>
              Resumen general
            </Typography>

            <Map />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Estadisticas;
